import React, { useState, useRef } from 'react';
import { storage, firestore } from '../../firebase';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { doc, updateDoc, collection, query, where, getDocs, getDoc, setDoc } from 'firebase/firestore';
import { isNativeApp, isMobileDevice } from '../../util/platformDetection';
import './ProfilePictureUpload.css';

const ProfilePictureUpload = ({ userId, photoUrl, pendingPhotoUrl, setPhotoUrl, setPendingPhotoUrl, size, onUploadComplete }) => {
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const fileInputRef = useRef(null);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) {
      // No file selected - user may have cancelled the selection
      return;
    }

    // Reset messages and set uploading state
    setErrorMessage('');
    setSuccessMessage('');
    setIsUploading(true);
    setUploadProgress(0);

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setErrorMessage('Please select a valid image file (JPEG, PNG, GIF, or WEBP)');
      setIsUploading(false);
      return;
    }

    // Validate file size (5MB max)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      setErrorMessage('Image size should be less than 5MB');
      setIsUploading(false);
      return;
    }

    try {
      // First delete any existing pending photo if one exists
      if (pendingPhotoUrl) {
        try {
          // Extract the file path from the URL
          const url = new URL(pendingPhotoUrl);
          const path = url.pathname.split('/o/')[1];
          if (path) {
            const decodedPath = decodeURIComponent(path.split('?')[0]);
            const pendingImageRef = ref(storage, decodedPath);
            await deleteObject(pendingImageRef);
            
          }
        } catch (error) {
          console.error('Error deleting previous pending profile picture:', error);
          // Continue with upload even if delete fails
        }
      }

      // Get file extension
      const fileExtension = file.name.split('.').pop().toLowerCase();
      
      // Add timestamp to ensure unique filenames for pending photos
      const timestamp = Date.now();
      
      // Create a storage reference with a unique path for pending photos
      const storageRef = ref(storage, `pendingProfilePictures/${userId}_${timestamp}.${fileExtension}`);

      // Start upload
      const uploadTask = uploadBytesResumable(storageRef, file);

      // Listen for upload progress
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
          setUploadProgress(progress);
        },
        (error) => {
          console.error('Upload error:', error);
          setErrorMessage('Failed to upload image. Please try again.');
          setIsUploading(false);
        },
        async () => {
          // Upload completed successfully
          try {
            // Get download URL
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            
            // Find the user document
            let userDocRef = null;
            let docExists = false;
            
            try {
              // Try using userId as document ID first
              userDocRef = doc(firestore, 'users', userId);
              const docSnap = await getDoc(userDocRef);
              
              if (docSnap.exists()) {
                docExists = true;
                
              } else {
                
                
                // Try to find by userId field
                const usersRef = collection(firestore, 'users');
                const q = query(usersRef, where('userId', '==', userId));
                const querySnapshot = await getDocs(q);
                
                if (!querySnapshot.empty) {
                  userDocRef = querySnapshot.docs[0].ref;
                  docExists = true;
                  
                } else {
                  // Last resort: try to find by handle
                  const handleQuery = query(usersRef, where('handle', '==', userId));
                  const handleSnapshot = await getDocs(handleQuery);
                  
                  if (!handleSnapshot.empty) {
                    userDocRef = handleSnapshot.docs[0].ref;
                    docExists = true;
                    
                  } else {
                    
                  }
                }
              }
            } catch (err) {
              console.error('Error finding user document:', err);
              // Continue with userDocRef as set, as a fallback
            }
            
            // Prepare update data
            const updateData = {
              pendingPhotoUrl: downloadURL,
              needsModeration: true,
              updatedAt: new Date()
            };
            
            // Update or create document
            if (docExists) {
              // Update existing document
              await updateDoc(userDocRef, updateData);
              
            } else {
              // Create new document with basic fields
              await setDoc(userDocRef, {
                userId: userId,
                pendingPhotoUrl: downloadURL,
                needsModeration: true,
                createdAt: new Date(),
                updatedAt: new Date()
              });
              
            }
            
            // Update state in parent component
            if (setPendingPhotoUrl) {
              setPendingPhotoUrl(downloadURL);
            }
            
            setSuccessMessage('Profile picture submitted for approval!');
            
            // Call the onUploadComplete callback if provided
            if (onUploadComplete) {
              onUploadComplete(downloadURL);
            }
          } catch (error) {
            console.error('Error updating Firestore:', error);
            setErrorMessage('Failed to save image. Please try again.');
          } finally {
            setIsUploading(false);
            // Reset file input
            if (fileInputRef.current) {
              fileInputRef.current.value = '';
            }
            // Auto-hide success message after 5 seconds
            if (successMessage) {
              setTimeout(() => setSuccessMessage(''), 5000);
            }
          }
        }
      );
    } catch (error) {
      console.error('Upload error:', error);
      setErrorMessage('Failed to upload image. Please try again.');
      setIsUploading(false);
    }
  };
  
  // Add an explicit function to trigger file selection
  const triggerFileInput = (e) => {
    // Prevent event bubbling to avoid conflicts
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (fileInputRef.current) {
      // Clear any previous error messages when user initiates photo selection
      setErrorMessage('');
      
      try {
        fileInputRef.current.click();
      } catch (error) {
        console.error('Error accessing camera/file input:', error);
        setErrorMessage('Unable to access camera. Please try again or select a photo from your gallery.');
      }
    }
  };

  return (
    <div className={`profile-picture-upload ${size || 'default'}`}>
      <div className="profile-picture-container" 
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer'
      }}
      >
        {pendingPhotoUrl ? (
          <>
            <img 
              src={pendingPhotoUrl} 
              alt="Pending profile" 
              className="profile-picture" 
              onClick={triggerFileInput}
              style={{ cursor: 'pointer' }}
            />
          </>
        ) : photoUrl ? (
          <img 
            src={photoUrl} 
            alt="Current profile" 
            className="profile-picture" 
            onClick={triggerFileInput}
            style={{ cursor: 'pointer' }}
          />
        ) : (
          <div 
            className="profile-picture placeholder" 
            onClick={triggerFileInput}
            style={{ cursor: 'pointer' }}
          >
            <i className="fas fa-user"></i>
          </div>
        )}
        
        <label className="upload-button">
          <input 
            type="file" 
            accept="image/jpeg, image/png, image/gif, image/webp" 
            onChange={handleImageUpload}
            disabled={isUploading}
            ref={fileInputRef}
          />
          <i className="fas fa-camera"></i>
        </label>
      </div>
      
      {/* Add a visible upload button below the profile picture */}
      <button 
        onClick={triggerFileInput}
        className="upload-profile-btn"
        disabled={isUploading}
      >
        <i className="fas fa-camera"></i>
        {pendingPhotoUrl ? 'Change Picture' : 'Upload Photo'}
      </button>
      
      {isUploading && (
        <div className="upload-progress">
          <div className="progress-bar">
            <div 
              className="progress-bar-fill" 
              style={{ width: `${uploadProgress}%` }}
            ></div>
          </div>
          <div className="progress-text">{uploadProgress}%</div>
        </div>
      )}
      
      {errorMessage && (
        <div className="upload-error">
          {errorMessage}
        </div>
      )}
      
      {successMessage && (
        <div className="upload-success">
          {successMessage}
        </div>
      )}
    </div>
  );
};

export default ProfilePictureUpload; 